<template>
  <div class="address-form">
    <AddressFormSaved
      v-if="hasSavedAddresses"
      :addresses="savedAddresses"
      :selectedAddressId="selectedSavedAddressId"
      :handleSubmit="handleSavedAddressFormSubmit"
      :isDisabled="isDisabled"
      :isSubmitButtonVisible="isSavedAddressesSubmitButtonVisible"
      :isSubmitButtonInactive="isSavedAddressesSubmitButtonInactive"
      :submitButtonText="submitButtonText"
      :hiddenAddressFields="hiddenSavedAddressFields"
      v-data-test-id="'address-form-saved'"
      @update="handleSavedAddressFormUpdate"
      @click-submit="$emit('click-submit')"
    >
      <div
        ref="formToggle"
        slot="below-radio"
        class="address-form__toggle-form"
        :class="{
          'address-form__toggle-form--active': isAddressFormVisible,
        }"
        v-data-test-id="'address-form__toggle-form'"
        @click="handleNewAddressFormShow"
      >
        <span class="address-form__plus icon-plus"></span>
        {{ $t('account.addresses.add_new') }}
      </div>
    </AddressFormSaved>

    <SmoothReflow ref="smoothReflow" :options="reflowOptions">
      <transition name="fade" @after-leave="onTransitionEnd">
        <AddressFormNew
          v-if="isAddressFormVisible"
          ref="addressFormNew"
          :address="newAddress"
          :countries="formCountries"
          :states="formCountryStates"
          :language="language"
          :handleSubmit="handleNewAddressFormSubmit"
          :isDisabled="isDisabled"
          :isSubmitButtonVisible="isNewAddressSubmitButtonVisible"
          :isScrollingToInvalidFieldOnSubmit="isScrollingToInvalidFieldOnSubmit"
          :submitButtonText="submitButtonText"
          :fieldValidations="addressFieldValidations"
          :fieldKeysOptional="addressFieldKeysOptional"
          :fieldKeysDisabled="addressFieldKeysDisabled"
          :fieldKeysOrder="addressFieldKeysOrder"
          :fieldKeysHidden="addressFieldKeysHidden"
          :fieldKeysHiddenInitially="addressFieldKeysHiddenInitially"
          :fieldKeysHalfWidth="addressFieldKeysHalfWidth"
          :restrictedCountriesForAutocomplete="restrictedCountriesForAutocomplete"
          v-data-test-id="'address-form-new'"
          @update="handleFieldUpdate"
          @update:isValid="handleIsValidUpdate"
          @click-submit="$emit('click-submit')"
        >
          <slot slot="below-address-form-new-fields" name="below-address-form-fields" />
        </AddressFormNew>
      </transition>
    </SmoothReflow>
  </div>
</template>

<script>
import {
  getAddressFieldKeysOptional,
  getAddressFieldKeysOrder,
  getAddressFieldKeysHidden,
  getAddressFieldKeysHalfWidth,
  areAddressesEqual,
} from '@/helpers/address'
import { scrollSmoothlyToElement } from '@/helpers/scroll'
import { sortArrayByPropName } from '@/helpers/array'
import { hasKeys, isEmpty } from '@/helpers/object'
import SmoothReflow from '@/components/_shared/SmoothReflow.vue'
import AddressFormNew from '@/components/_shared/Form/Forms/AddressFormNew.vue'

const DEFAULT_SAVED_ADDRESS_ID = -1

const addressValidator = obj =>
  isEmpty(obj) ||
  !obj.countryData ||
  hasKeys(obj.countryData, ['iso', 'name', 'hasStates', 'requireZipcode', 'requireStates'])

export default {
  name: 'AddressForm',
  components: {
    AddressFormSaved: () =>
      import(
        /* webpackChunkName: "AddressFormSaved" */ '@/components/_shared/Form/Forms/AddressFormSaved.vue'
      ),
    AddressFormNew,
    SmoothReflow,
  },

  props: {
    address: {
      type: Object,
      default: () => ({}),
      validator: addressValidator,
    },

    defaultNewAddress: {
      type: Object,
      default: () => ({}),
      validator: addressValidator,
    },

    defaultSavedAddress: {
      type: Object,
      default: () => ({}),
      validator: addressValidator,
    },

    savedAddresses: {
      type: Array,
      default: () => [],
    },

    countries: {
      type: Array,
      required: true,
    },

    countryStates: {
      type: Array,
      required: true,
    },

    language: {
      type: String,
      default: '',
    },

    countriesForAutoComplete: {
      type: Array,
    },

    isSubmitButtonVisible: {
      type: Boolean,
      default: false,
    },

    isSavedAddressesSubmitButtonInactive: {
      type: Boolean,
      default: false,
    },

    submitButtonText: {
      type: String,
      required: true,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    isScrollingToInvalidFieldOnSubmit: {
      type: Boolean,
      default: true,
    },

    handleUpdate: {
      type: Function,
      required: true,
    },

    handleFieldUpdate: {
      type: Function,
      required: true,
    },

    handleIsValidUpdate: {
      type: Function,
      default: () => {},
    },

    handleSubmit: {
      type: Function,
      required: true,
    },

    addressFieldValidations: {
      type: Object,
    },

    addressFieldKeysDisabled: {
      type: Array,
    },

    reflowOptions: {
      type: Object,
      default: () => ({
        property: ['height'],
        transition: 'height 500ms ease-out',
      }),
    },

    hiddenSavedAddressFields: {
      type: Array,
    },
  },

  computed: {
    newAddress() {
      return this.savedAddress ? {} : this.address
    },

    savedAddress() {
      return this.getSavedAddressById(this.address?.id)
    },

    countryData() {
      return this.address?.countryData
    },

    selectedSavedAddressId() {
      return this.savedAddress?.id || DEFAULT_SAVED_ADDRESS_ID
    },

    isAddressFormVisible() {
      return !this.hasSavedAddresses || !isEmpty(this.newAddress)
    },

    hasSavedAddresses() {
      return this.savedAddresses.length > 0
    },

    isSavedAddressesSubmitButtonVisible() {
      return this.isSubmitButtonVisible && !this.isAddressFormVisible
    },

    isNewAddressSubmitButtonVisible() {
      return this.isSubmitButtonVisible
    },

    formCountries() {
      return sortArrayByPropName(this.geoLocationToFormSelect(this.countries), 'text')
    },

    restrictedCountriesForAutocomplete() {
      return this.countriesForAutoComplete
        ? this.countriesForAutoComplete
        : this.countries.map(({ iso }) => iso)
    },

    formCountryStates() {
      return sortArrayByPropName(this.geoLocationToFormSelect(this.countryStates), 'value')
    },

    addressFieldKeysOptional() {
      return getAddressFieldKeysOptional(this.countryData)
    },

    addressFieldKeysOrder() {
      return getAddressFieldKeysOrder(this.countryData)
    },

    addressFieldKeysHidden() {
      return getAddressFieldKeysHidden(this.countryData)
    },

    addressFieldKeysHiddenInitially() {
      return [
        'address2',
        'city',
        'state_id',
        'country_id',
        'zipcode',
        'phone',
        'neighborhood',
        'document_number',
      ]
    },

    addressFieldKeysHalfWidth() {
      return getAddressFieldKeysHalfWidth(this.countryData)
    },
  },

  watch: {
    address: {
      immediate: true,
      handler() {
        this.updateDefaultNewAddress()
        this.updateDefaultSavedAddress()
      },
    },

    savedAddresses: {
      immediate: true,
      handler() {
        this.updateDefaultSavedAddress()
      },
    },

    defaultNewAddress: {
      immediate: true,
      handler(newVal, oldVal) {
        this.updateDefaultNewAddress(oldVal)
      },
    },

    defaultSavedAddress: {
      immediate: true,
      handler(newVal, oldVal) {
        this.updateDefaultSavedAddress(oldVal)
      },
    },
  },

  methods: {
    /* public methods */
    async validate() {
      await this.$refs.addressFormNew?.validate()
    },

    async setError(error) {
      await this.$refs.addressFormNew?.setError(error)
    },

    /* private methods */
    updateDefaultNewAddress(previousDefaultAddress = {}) {
      if (
        !this.hasSavedAddresses &&
        !isEmpty(this.defaultNewAddress) &&
        !this.areAddressesEqual(this.address, this.defaultNewAddress) &&
        (isEmpty(this.address) || this.areAddressesEqual(this.address, previousDefaultAddress))
      ) {
        this.handleUpdate(this.defaultNewAddress)
      }
    },

    updateDefaultSavedAddress(previousDefaultAddress = {}) {
      if (
        this.hasSavedAddresses &&
        !isEmpty(this.defaultSavedAddress) &&
        !this.areAddressesEqual(this.address, this.defaultSavedAddress) &&
        (isEmpty(this.address) || this.areAddressesEqual(this.address, previousDefaultAddress))
      ) {
        this.handleUpdate(this.defaultSavedAddress)
      }
    },

    async handleNewAddressFormShow() {
      this.handleUpdate(this.defaultNewAddress)
      scrollSmoothlyToElement(this.$refs.formToggle, {
        duration: On.TransitionTimes.slower,
        offset: 50,
      })
      await this.$nextTick()
      this.$refs.addressFormNew?.focusFirstInvalidField()
    },

    handleSavedAddressFormUpdate(selectedAddressId) {
      const selectedAddress = this.getSavedAddressById(selectedAddressId)
      this.handleUpdate(selectedAddress)
    },

    async handleSavedAddressFormSubmit(selectedAddressId) {
      const selectedAddress = this.getSavedAddressById(selectedAddressId)
      return this.handleSubmit(selectedAddress, { isSavedAddress: true })
    },

    async handleNewAddressFormSubmit(newAddress) {
      return this.handleSubmit(newAddress, { isSavedAddress: false })
    },

    onTransitionEnd() {
      // manually invoke smoothReflow update only after the fade animation ended
      // before that the height did not change so that smoothReflow would not update
      // smoothReflow transitionEvent option did not work, because there is no transitionend event triggered
      this.$refs.smoothReflow?.$options.updated.forEach(updated =>
        updated.apply(this.$refs.smoothReflow)
      )
    },

    areAddressesEqual(address1, address2) {
      return (!address1 && !address2) || areAddressesEqual(address1, address2)
    },

    getSavedAddressById(addressId) {
      return this.savedAddresses.find(({ id }) => id === addressId)
    },

    geoLocationToFormSelect(geoLocation) {
      return geoLocation.map(({ id, name, nameTranslated, iso }) => ({
        text: nameTranslated || name,
        value: id,
        iso,
      }))
    },
  },
}
</script>

<style lang="scss" scoped>
.address-form {
  &__toggle-form {
    width: 100%;
    margin-top: 16px;
    padding: 13px 21px 14px;
    border: 1px solid $color-grey-light-5;
    border-radius: 2px;
    font-size: $font-size-small;
    color: $color-grey-dark-3;
    line-height: $line-height-normal;
    font-weight: bold;
    cursor: pointer;
    @include transition((border-color), $transition-time-fast);

    &:hover,
    &--active {
      border-color: $color-grey-dark-2;
    }
  }

  &__plus {
    margin-right: 18px;
    font-size: $font-size-xsmall;
  }
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-time ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
